<script>
import axios from '@/utils/axios';

const DEFAULT_FORM = () => ({
  title: '',
  content: '',
  type: 'general',
  branch_id: '',
  published_at: '',
  expires_at: '',
});

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      mode: 'create',       // 'create' | 'edit'
      editId: null,
      form: DEFAULT_FORM(),
      errors: {},
      branches: [],
      typeOptions: ['general', 'urgent', 'event', 'policy'],
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    },
  },
  mounted() {
    this.fetchBranches();
  },
  methods: {
    async fetchBranches() {
      try {
        const res = await axios.get('/hrms/branches/dropdown');
        this.branches = res.data.data || [];
      } catch (error) {
        // branch dropdown is optional - fail silently if endpoint isn't available
        console.log(error.message);
      }
    },

    toInputDate(val) {
      if (!val) return '';
      return String(val).slice(0, 10);
    },

    // ----- open in CREATE mode -----
    create() {
      this.mode = 'create';
      this.editId = null;
      this.form = DEFAULT_FORM();
      this.errors = {};
    },

    // ----- open in EDIT mode -----
    edit(row) {
      this.mode = 'edit';
      this.editId = row.id;
      this.form = {
        title: row.title || '',
        content: row.content || '',
        type: row.type || 'general',
        branch_id: row.branch_id || '',
        published_at: this.toInputDate(row.published_at),
        expires_at: this.toInputDate(row.expires_at),
      };
      this.errors = {};
    },

    buildPayload() {
      return {
        title: this.form.title,
        content: this.form.content,
        type: this.form.type,
        branch_id: this.form.branch_id || null,
        published_at: this.form.published_at || null,
        expires_at: this.form.expires_at || null,
      };
    },

    async save() {
      this.errors = {};
      this.setLoading(true);
      try {
        let response;
        if (this.isEdit) {
          response = await axios.put(`/hrms/announcements/${this.editId}`, this.buildPayload());
        } else {
          response = await axios.post('/hrms/announcements', this.buildPayload());
        }
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_announcement_modal');
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <div class="modal fade" id="hrm_announcement_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ isEdit ? 'Edit Announcement' : 'Add Announcement' }}</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="save">
          <div class="modal-body pb-0">

            <!-- Title -->
            <div class="mb-3">
              <label class="form-label">Title <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="form.title" placeholder="e.g. Office closed on 26th Jan" />
              <div v-if="errors.title" class="text-danger small mt-1">{{ errors.title[0] }}</div>
            </div>

            <!-- Content -->
            <div class="mb-3">
              <label class="form-label">Content <span class="text-danger">*</span></label>
              <textarea class="form-control" rows="4" v-model="form.content" placeholder="Announcement details..."></textarea>
              <div v-if="errors.content" class="text-danger small mt-1">{{ errors.content[0] }}</div>
            </div>

            <div class="row">
              <!-- Type -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="form.type">
                  <option v-for="t in typeOptions" :key="t" :value="t">{{ t.charAt(0).toUpperCase() + t.slice(1) }}</option>
                </select>
                <div v-if="errors.type" class="text-danger small mt-1">{{ errors.type[0] }}</div>
              </div>

              <!-- Branch -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Branch <span style="color:#94a3b8; font-weight:400;">(optional — all branches if empty)</span></label>
                <select class="form-select" v-model="form.branch_id">
                  <option value="">All Branches</option>
                  <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select>
                <div v-if="errors.branch_id" class="text-danger small mt-1">{{ errors.branch_id[0] }}</div>
              </div>
            </div>

            <div class="row">
              <!-- Published At -->
              <div class="col-md-6 mb-3">
                <label class="form-label">
                  Publish Date
                  <span style="color:#94a3b8; font-weight:400;">(leave empty to publish later)</span>
                </label>
                <input type="date" class="form-control" v-model="form.published_at" />
                <div v-if="errors.published_at" class="text-danger small mt-1">{{ errors.published_at[0] }}</div>
              </div>

              <!-- Expires At -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Expiry Date</label>
                <input type="date" class="form-control" v-model="form.expires_at" />
                <div v-if="errors.expires_at" class="text-danger small mt-1">{{ errors.expires_at[0] }}</div>
              </div>
            </div>

            <div class="ann-note mb-3">
              <i class="ti ti-info-circle me-1"></i>
              Leave "Publish Date" empty to save this as a draft. Use the <strong>Publish</strong> action from the list to publish it immediately.
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ isEdit ? 'Update' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ann-note {
  font-size: 11.5px; color: #92722a;
  background: #fffbeb; border: 1px solid #fde68a;
  border-radius: 8px; padding: 9px 12px; line-height: 1.5;
}
</style>